import { useState } from 'react'
import { Button, ContentCard, Stack } from '@forwardflow/ui-v2'
import { Grid } from '@mantine/core'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { ArrowLeftMdIcon } from '@forwardflow/icons'
import { motion } from 'framer-motion'
import { UserService } from '@forwardflow/services/UserService'
import { completeAgentOnboarding } from './completeAgentOnboarding'

export const GettingStarted = () => {
   const { getPreviousStep } = useOnboardingStoreV2()
   const [completing, setCompleting] = useState(false)
   const [vcardSent, setVcardSent] = useState(false)
   const [vcardLoading, setVcardLoading] = useState(false)

   const handleSendCard = async () => {
      try {
         setVcardLoading(true)
         await UserService.sendCard()
         setVcardSent(true)
      } catch (error) {
         console.error('Failed to send card:', error)
      } finally {
         setVcardLoading(false)
      }
   }

   const handleResend = async () => {
      setVcardLoading(true)
      try {
         await UserService.sendCard()
      } catch (error) {
         console.error('Failed to resend card:', error)
      } finally {
         setVcardLoading(false)
      }
   }

   return (
      <Stack gap={32}>
         <ContentCard
            className="space-y-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <div>
               <p className="text-white font-semibold text-lg mb-1">
                  See how Forward Flow works
               </p>
               <p className="text-[#99A1AF] text-sm font-medium">
                  Watch a quick walkthrough to understand how live transfers connect you with leads
               </p>
            </div>

            <div className="w-full rounded-xl overflow-hidden border border-white/10">
               <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                     src="https://www.youtube.com/embed/7sm4AbskI6M"
                     title="See how Forward Flow works"
                     allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                     allowFullScreen
                     className="absolute inset-0 w-full h-full"
                  />
               </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
               <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                     <path d="M17 2H7C5.9 2 5 2.9 5 4V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V4C19 2.9 18.1 2 17 2ZM12 20C11.45 20 11 19.55 11 19C11 18.45 11.45 18 12 18C12.55 18 13 18.45 13 19C13 19.55 12.55 20 12 20ZM17 17H7V5H17V17Z" fill="#A684FF"/>
                  </svg>
               </div>
               <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm mb-1">
                     Save the Forward Flow contact number
                  </p>
                  <p className="text-[#99A1AF] text-xs">
                     So that you can take live transfers right away
                  </p>
               </div>
               <div className="flex items-center gap-3 flex-shrink-0">
                  {vcardSent ? (
                     <>
                        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00D492]/20 border border-[#00D492]/40 text-[#00D492] text-xs font-medium">
                           <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#00D492"/>
                           </svg>
                           Vcard Sent
                        </span>
                        <button
                           onClick={handleResend}
                           disabled={vcardLoading}
                           className="text-[#99A1AF] text-xs font-medium hover:text-white transition-colors disabled:opacity-50"
                        >
                           {vcardLoading ? 'Sending...' : 'Resend'}
                        </button>
                     </>
                  ) : (
                     <button
                        onClick={handleSendCard}
                        disabled={vcardLoading}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50"
                     >
                        {vcardLoading ? 'Sending...' : 'Send me the Forward Flow Vcard'}
                     </button>
                  )}
               </div>
            </div>

         </ContentCard>

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     disabled={completing}
                     onClick={async () => {
                        try {
                           setCompleting(true)
                           await completeAgentOnboarding()
                        } catch (error) {
                           console.error('Failed to complete agent onboarding:', error)
                        } finally {
                           window.location.href = '/dashboard'
                        }
                     }}
                  >
                     {completing ? 'Please wait...' : 'Start using Forward Flow →'}
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}
